/* eslint-disable react/forbid-prop-types */
import React from "react";
import PropTypes from "prop-types";
import PublicationLinks from "../../../../components/Publications/components/PublicationLinks";
import SortableHeader from "../../../../components/atoms/SortableHeader";
import {
  sortStr,
  sortNum,
  sortNumDate,
  sortScientificNumber,
} from "../../../../common/helpers/helpers";
import { sortRefs } from "../../../../common/helpers/sortingHelpers";

const sortByDate = (a, b) => sortNumDate(a.date, b.date);
const sortByDisease = (a, b) => sortStr(a.diseaseOrTrait, b.diseaseOrTrait);
const sortByOddsRatio = (a, b) => sortNum(a.odds_ratio, b.odds_ratio);
const sortByPValue = (a, b) => sortScientificNumber(a.p_value, b.p_value);
const sortBySnp = (a, b) =>
  sortStr(a.strongestRiskAllele, b.strongestRiskAllele);
const sortByConfidence = (a, b) => {
  const lowDiff = a.confidenceLow - b.confidenceLow;
  const highDiff = a.confidenceHigh - b.confidenceHigh;
  const aConfComment = a.confidenceComment || "";
  const bConfComment = b.confidenceComment || "";
  const commentDiff = aConfComment.localeCompare(bConfComment);
  return lowDiff || highDiff || commentDiff;
};

function getConfidenceContents({
  confidenceLow,
  confidenceHigh,
  confidenceComment,
}) {
  if (!confidenceComment && !confidenceHigh && !confidenceLow) {
    return "-";
  }
  return (
    <React.Fragment>
      {[confidenceLow, confidenceHigh]
        .filter((x) => x === 0 || Boolean(x))
        .join(" - ")}
      <div>{confidenceComment}</div>
    </React.Fragment>
  );
}

function GWASTable({
  items,
  labels,
  openGwasModal,
  sortedField,
  isAsc,
  toggleField,
}) {
  return (
    <table className="varsome-table varsome-table-sorted">
      <thead>
        <tr>
          <SortableHeader
            sort={(key) => toggleField(sortByDate, key)}
            labels={labels}
            sortedField={sortedField}
            isAsc={isAsc}
            field="date"
            width="12%"
            align="left"
          />
          <SortableHeader
            sort={(key) => toggleField(sortByDisease, key)}
            labels={labels}
            sortedField={sortedField}
            isAsc={isAsc}
            field="diseaseOrTrait"
            width="16%"
            align="left"
          />
          <SortableHeader
            sort={(key) => toggleField(sortByOddsRatio, key)}
            labels={labels}
            sortedField={sortedField}
            isAsc={isAsc}
            field="oddsRatio"
            width="11%"
            align="center"
          />
          <SortableHeader
            sort={(key) => toggleField(sortByPValue, key)}
            labels={labels}
            sortedField={sortedField}
            isAsc={isAsc}
            field="pValue"
            width="12%"
            align="center"
          />
          <SortableHeader
            sort={(key) => toggleField(sortByConfidence, key)}
            labels={labels}
            sortedField={sortedField}
            isAsc={isAsc}
            field="confidenceRange"
            width="16%"
            align="center"
          />
          <SortableHeader
            sort={(key) => toggleField(sortBySnp, key)}
            labels={labels}
            sortedField={sortedField}
            isAsc={isAsc}
            field="strongestRiskAllele"
            width="12%"
            align="center"
          />
          <SortableHeader
            sort={(key) => toggleField(sortRefs, key)}
            labels={labels}
            sortedField={sortedField}
            isAsc={isAsc}
            field="pubMedReferences"
            width="10%"
            align="center"
          />
          <th width="11%" />
        </tr>
      </thead>
      <tbody>
        {items.map((item) => (
          <tr key={item.id}>
            <td>{item.readableDate || "-"}</td>
            <td>{item.diseaseOrTrait || "-"}</td>
            <td className="varsome-text-center">{item.oddsRatio || "-"}</td>
            <td className="varsome-text-center">{item.pValue || "-"}</td>
            <td className="varsome-text-center">
              {getConfidenceContents(item)}
            </td>
            <td className="varsome-text-center">
              {item.strongestRiskAllele || "-"}
            </td>
            <td className="varsome-text-center">
              {item.pubMedRefs ? (
                <PublicationLinks ids={item.pubMedRefs} />
              ) : (
                "-"
              )}
            </td>
            <td className="varsome-text-center">
              <a onClick={() => openGwasModal(item)}>More...</a>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

GWASTable.propTypes = {
  labels: PropTypes.shape({
    pubMedReferences: PropTypes.string,
    date: PropTypes.string,
    pValue: PropTypes.string,
    diseaseOrTrait: PropTypes.string,
    strongestSnpRiskAllele: PropTypes.string,
  }),
  items: PropTypes.arrayOf(
    PropTypes.shape({
      strongestRiskAllele: PropTypes.string,
      readableDate: PropTypes.string,
      pubMedRefs: PropTypes.arrayOf(PropTypes.any),
      oddsRatio: PropTypes.string,
      pValue: PropTypes.string,
      diseaseOrTrait: PropTypes.string,
      confidenceLow: PropTypes.string,
      confidenceHigh: PropTypes.string,
      confidenceComment: PropTypes.string,
      id: PropTypes.string,
    })
  ),
  openGwasModal: PropTypes.func.isRequired,
  sortedField: PropTypes.string,
  isAsc: PropTypes.bool,
  toggleField: PropTypes.func.isRequired,
};

GWASTable.defaultProps = {
  labels: {},
  items: [],
  sortedField: null,
  isAsc: true,
};

export default GWASTable;
